import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Users, Activity, ExternalLink, ShieldCheck, ChevronRight, PieChart, Video, Building, CalendarClock } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import { regionsMapData, RegionMapData } from "@/data/mapData";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { getSectionContent } from "@/data/cmsStore";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { moroccoRegions } from "@/data/parties";
import { toast } from "sonner";

const ElectoralMap = ({ previewData }: { previewData?: Record<string, string> }) => {
  const { lang, isRtl } = useI18n();
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState<RegionMapData | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  
  // Custom states for 'Elections territoriales'
  const [selectedProvince, setSelectedProvince] = useState<string>("");
  const [selectedElectionType, setSelectedElectionType] = useState<string>("");
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isMouseOverMap, setIsMouseOverMap] = useState(false);

  // CMS State
  const [cmsData, setCmsData] = useState(getSectionContent("map"));

  useEffect(() => {
    const handleCmsUpdate = () => {
      setCmsData(getSectionContent("map"));
    };
    window.addEventListener("cmsLayoutDraftChanged", handleCmsUpdate);
    window.addEventListener("cmsLayoutChanged", handleCmsUpdate);
    return () => {
      window.removeEventListener("cmsLayoutDraftChanged", handleCmsUpdate);
      window.removeEventListener("cmsLayoutChanged", handleCmsUpdate);
    };
  }, []);

  const activeData = previewData || cmsData;
  const model = activeData.model || "network";
  const territorialesEnabled = activeData.territoriales === "true";
  const title = lang === 'ar' ? (activeData.title_ar || "خريطة انتخابية تفاعلية") : (activeData.title_fr || "Carte Électorale Interactive");
  const subtitle = lang === 'ar' ? (activeData.subtitle_ar || "تعمق في البيانات الإقليمية.") : (activeData.subtitle_fr || "Plongez au cœur des données régionales.");
  
  let customLinks: Record<string, string>[] = [];
  try {
    customLinks = JSON.parse(activeData.custom_links || "[]");
  } catch (e) { console.error(e); }

  let regionStatsData: Record<string, Record<string, string>[]> = {};
  try {
    regionStatsData = JSON.parse(activeData.region_stats || "{}");
  } catch (e) { console.error(e); }

  const handleRouteToVoter = () => {
    let query = `?region=${encodeURIComponent(selectedRegion?.name || '')}`;
    if (territorialesEnabled && selectedProvince) query += `&province=${encodeURIComponent(selectedProvince)}`;
    if (territorialesEnabled && selectedElectionType) query += `&type=${encodeURIComponent(selectedElectionType)}`;
    navigate(`/resultats-candidats${query}`);
    setSelectedRegion(null);
  };

  const handleRouteToEvents = () => {
    let query = `?region=${encodeURIComponent(selectedRegion?.name || '')}`;
    if (territorialesEnabled && selectedProvince) query += `&province=${encodeURIComponent(selectedProvince)}`;
    navigate(`/evenements/partis${query}`);
    setSelectedRegion(null);
  };

  const handleShowResults = () => {
    toast.info(lang === 'ar' ? "النتائج المفصلة ستكون متاحة ابتداء من 23 سبتمبر 2026." : "Les résultats détaillés seront disponibles à partir du 23 Septembre 2026.");
  };

  const currentRegionProvinces = moroccoRegions.find(r => r.name === selectedRegion?.name)?.provinces || [];

  return (
    <section className="py-16 bg-gradient-to-b from-background to-muted/30">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-muted-foreground font-body max-w-2xl mx-auto text-lg">
            {subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-center bg-card rounded-3xl border border-border shadow-2xl overflow-hidden p-4 md:p-8 relative min-h-[500px]">
          
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

          {/* Map Visuals */}
          <div 
            className="lg:col-span-8 relative w-full h-[500px] md:h-[600px] group"
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }}
            onMouseEnter={() => setIsMouseOverMap(true)}
            onMouseLeave={() => setIsMouseOverMap(false)}
          >
            
            {model === "network" && (
              <div className="absolute inset-0 bg-transparent rounded-2xl overflow-hidden">
                <svg className="w-full h-full absolute inset-0 drop-shadow-md" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                  {regionsMapData.map((region, i) => {
                    const isHovered = hoveredRegion === region.id;
                    const isSelected = selectedRegion?.id === region.id;
                    return (
                      <motion.g 
                        key={region.id}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.05, type: "spring" }}
                        onClick={() => setSelectedRegion(region)}
                        onMouseEnter={() => setHoveredRegion(region.id)}
                        onMouseLeave={() => setHoveredRegion(null)}
                        className="cursor-pointer"
                      >
                        {(isHovered || isSelected) && (
                          <circle cx={region.x} cy={region.y} r="5.5" fill="none" stroke={region.color} strokeWidth="0.5" className="animate-ping opacity-75" />
                        )}
                        <circle cx={region.x} cy={region.y} r={isHovered || isSelected ? "4.5" : "3.8"} fill={region.color} className="transition-all duration-300 drop-shadow-sm hover:drop-shadow-lg" />
                        <text x={region.x} y={region.y + 0.5} dominantBaseline="middle" textAnchor="middle" fill="#ffffff" className="text-[3.5px] font-bold font-display transition-all duration-300 pointer-events-none select-none">
                          {region.num}
                        </text>
                        {/* Mobile: Nom toujours visible avant le clic, caché une fois cliqué et masqué sur PC car le tooltip de survol prend le relais */}
                        <text 
                          x={region.x + 6} 
                          y={region.y} 
                          fill={region.color} 
                          className={`text-[2.2px] font-bold font-body transition-all duration-300 pointer-events-none drop-shadow-sm select-none lg:hidden ${selectedRegion ? 'opacity-0' : 'opacity-100'}`}
                          dominantBaseline="middle"
                        >
                           {lang === 'ar' ? region.nameAr : region.name}
                        </text>
                      </motion.g>
                    );
                  })}
                </svg>
                
                {/* Desktop Tooltip: Seulement visible sur PC (hidden lg:block) et quand on survole la carte */}
                {(hoveredRegion && !selectedRegion && isMouseOverMap) && (
                  <div 
                    className="hidden lg:block absolute pointer-events-none z-[100] transform -translate-x-1/2 -translate-y-[130%] transition-transform duration-75"
                    style={{ left: mousePos.x, top: mousePos.y }}
                  >
                    {(() => {
                      const active = regionsMapData.find(r => r.id === hoveredRegion);
                      if (!active) return null;
                      return (
                        <div className="bg-background/95 backdrop-blur-xl px-4 pt-1.5 pb-2 rounded-2xl border-2 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] min-w-max" style={{ borderColor: active.color }}>
                          <span className="font-display font-extrabold text-xl md:text-2xl whitespace-nowrap" style={{ color: active.color }}>
                            {lang === 'ar' ? active.nameAr : active.name}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {model === "grid" && (
              <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-3 gap-3 p-2 overflow-y-auto custom-scrollbar">
                {regionsMapData.map((region, i) => (
                  <motion.div
                    key={region.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => setSelectedRegion(region)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col items-center text-center gap-2 ${
                      selectedRegion?.id === region.id ? 'border-primary bg-primary/10' : 'border-border/50 bg-card hover:border-primary/30 hover:bg-muted'
                    }`}
                  >
                    <MapPin className={`w-6 h-6 ${selectedRegion?.id === region.id ? 'text-primary' : 'text-accent'}`} />
                    <span className="font-bold text-sm">{lang === 'ar' ? region.nameAr : region.name}</span>
                    <span className="text-xs text-muted-foreground">{region.stats.seats} Sièges</span>
                  </motion.div>
                ))}
              </div>
            )}

            {model === "list" && (
              <div className="absolute inset-0 flex flex-col space-y-2 p-2 overflow-y-auto custom-scrollbar">
                {regionsMapData.map((region, i) => (
                  <motion.div
                    key={region.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => setSelectedRegion(region)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-between ${
                      selectedRegion?.id === region.id ? 'border-primary bg-primary/10' : 'border-border/50 bg-card hover:border-primary/30 hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedRegion?.id === region.id ? 'bg-primary/20 text-primary' : 'bg-muted text-foreground'}`}>
                        <span className="font-bold text-xs">{i+1}</span>
                      </div>
                      <span className="font-bold">{lang === 'ar' ? region.nameAr : region.name}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </motion.div>
                ))}
              </div>
            )}

          </div>

          <div className="hidden lg:flex lg:col-span-4 h-full flex-col justify-center z-10 w-full">
            <div className="bg-card/80 p-5 rounded-2xl border border-border backdrop-blur-md shadow-sm h-auto">
              <h3 className="text-xl font-display font-bold text-foreground mb-2 md:mb-4 border-b border-border pb-2">
                {lang === 'ar' ? "جهـات المملكـة" : "Légende des Régions"}
              </h3>
              
              <div className="flex flex-col gap-0.5 md:gap-1" dir={isRtl ? "rtl" : "ltr"}>
                {[...regionsMapData].sort((a, b) => a.num - b.num).map((region) => (
                  <button 
                    key={region.id}
                    onClick={() => setSelectedRegion(region)}
                    onMouseEnter={() => setHoveredRegion(region.id)}
                    onMouseLeave={() => setHoveredRegion(null)}
                    className={`flex items-center gap-2 w-full text-left p-1 md:py-1.5 px-2 rounded-lg transition-all ${
                      (selectedRegion?.id === region.id || hoveredRegion === region.id) 
                        ? 'bg-muted scale-[1.02]' 
                        : 'hover:bg-muted/50 hover:scale-[1.01]'
                    }`}
                  >
                    <div 
                      className="w-4 h-4 md:w-5 md:h-5 rounded flex items-center justify-center shrink-0 shadow-sm"
                      style={{ backgroundColor: region.color }}
                    />
                    <span className="font-semibold text-sm text-foreground flex-1">
                      <span className="font-bold opacity-80 mr-1.5 md:mr-2">{region.num}</span> 
                      {lang === 'ar' ? region.nameAr : region.name}
                    </span>
                  </button>
                ))}
              </div>

              {customLinks && customLinks.length > 0 && (
                <div className="pt-4 border-t border-primary/10 mt-4 space-y-2">
                  {customLinks.map((btn, idx) => (
                    <Button key={idx} variant="outline" className="w-full justify-between" asChild>
                      <a href={btn.url} target="_blank" rel="noreferrer">
                        {lang === 'ar' ? btn.label_ar : btn.label_fr}
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Sheet open={selectedRegion !== null} onOpenChange={(open) => {
        if(!open) {
          setSelectedRegion(null);
          setSelectedProvince("");
          setSelectedElectionType("");
        }
      }}>
        <SheetContent side={isRtl ? "left" : "right"} className="w-full sm:max-w-md border-l-border/50 bg-card/95 backdrop-blur-xl custom-scrollbar overflow-y-auto z-50">
          {selectedRegion && (
            <div className="pt-6" dir={isRtl ? "rtl" : "ltr"}>
              <SheetHeader className="mb-6 text-left">
                <Badge variant="outline" className="w-fit mb-2 text-primary border-primary bg-primary/5">
                  Région Administrative
                </Badge>
                <SheetTitle className="text-2xl font-display font-bold leading-tight">
                  {lang === 'ar' ? selectedRegion.nameAr : selectedRegion.name}
                </SheetTitle>
              </SheetHeader>

              {territorialesEnabled && (
                <div className="space-y-4 mb-6 p-4 bg-muted/50 rounded-xl border border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-accent" />
                    <span className="text-sm font-bold">Élections Territoriales</span>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-muted-foreground">Sélectionnez une Province (Optionnel)</label>
                      <Select value={selectedProvince} onValueChange={setSelectedProvince}>
                        <SelectTrigger className="w-full bg-background">
                          <SelectValue placeholder="Toutes les provinces" />
                        </SelectTrigger>
                        <SelectContent className="z-[60]">
                          <SelectItem value="all">Toutes les provinces</SelectItem>
                          {currentRegionProvinces.map(prov => (
                            <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-muted-foreground">Type d'Élection (Optionnel)</label>
                      <Select value={selectedElectionType} onValueChange={setSelectedElectionType}>
                        <SelectTrigger className="w-full bg-background">
                          <SelectValue placeholder="Tous types" />
                        </SelectTrigger>
                        <SelectContent className="z-[60]">
                          <SelectItem value="all">Tous types</SelectItem>
                          <SelectItem value="regionale">Élections Régionales</SelectItem>
                          <SelectItem value="provinciale">Élections Provinciales</SelectItem>
                          <SelectItem value="communale">Élections Communales</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                
                {regionStatsData[selectedRegion.id] && regionStatsData[selectedRegion.id].length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {regionStatsData[selectedRegion.id].map((stat: Record<string, string>, idx: number) => (
                      <div key={idx} className="bg-muted p-4 rounded-2xl border border-border/50 flex flex-col items-start">
                        <Activity className="w-5 h-5 text-accent mb-2" />
                        <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">
                          {lang === 'ar' ? stat.title_ar : stat.title_fr}
                        </span>
                        <span className="text-xl font-bold text-foreground">
                          {lang === 'ar' ? stat.value_ar : stat.value_fr}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-muted p-4 rounded-2xl border border-border/50 flex flex-col">
                        <Users className="w-5 h-5 text-accent mb-2" />
                        <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">Inscrits</span>
                        <span className="text-xl font-bold text-foreground">{(selectedRegion.stats.registeredVoters / 1000000).toFixed(1)} M</span>
                      </div>
                      
                      <div className="bg-muted p-4 rounded-2xl border border-border/50 flex flex-col">
                        <ShieldCheck className="w-5 h-5 text-emerald-500 mb-2" />
                        <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">Sièges (2021)</span>
                        <span className="text-xl font-bold text-foreground">{selectedRegion.stats.seats}</span>
                      </div>
                    </div>

                    <div className="bg-primary/5 p-5 rounded-2xl border border-primary/20">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-semibold flex items-center gap-2">
                          <Activity className="w-4 h-4 text-primary" /> Participation (2021)
                        </span>
                        <span className="text-lg font-bold text-primary">{selectedRegion.stats.participation2021}%</span>
                      </div>
                      <div className="w-full bg-primary/20 rounded-full h-2.5 overflow-hidden">
                        <motion.div 
                          className="bg-primary h-2.5 rounded-full" 
                          initial={{ width: 0 }}
                          animate={{ width: `${selectedRegion.stats.participation2021}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="pt-2 flex flex-col gap-3">
                  <Button onClick={handleRouteToVoter} className="w-full py-6 font-semibold text-white shadow-lg gap-2 group text-base hover:opacity-90">
                    {lang === 'ar' ? "تصفح مرشحي 2026" : "Consulter les Candidats 2026"}
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                  </Button>
                  <Button onClick={handleRouteToEvents} variant="outline" className="w-full py-6 font-semibold gap-2 border-border/80 hover:bg-muted text-foreground">
                    {lang === 'ar' ? "أحداث في هذه الجهة" : "Événements dans cette région"}
                  </Button>
                  <Button onClick={handleShowResults} variant="secondary" className="w-full py-6 font-semibold gap-2 text-primary border border-primary/20 bg-primary/5 hover:bg-primary/10">
                    <PieChart className="w-4 h-4" /> {lang === 'ar' ? "نتائج 2026" : "Résultats 2026"}
                  </Button>
                  
                  <div className="pt-4 mt-2 border-t border-border flex flex-col gap-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 text-center">
                      {lang === 'ar' ? "اكتشف أيضاً" : "Découvrir aussi"}
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                        <Button variant="link" size="sm" asChild className="text-xs text-muted-foreground hover:text-primary">
                           <Link to="/evenements/partis">
                             <CalendarClock className="w-3 h-3 me-1"/> {lang === 'ar' ? "الأجندة الوطنية" : lang === 'zgh' ? "ⴰⵖⴰⵡⴰⵙ ⴰⵏⴰⵎⵓⵔ" : "Agenda national"}
                           </Link>
                        </Button>
                        <Button variant="link" size="sm" asChild className="text-xs text-muted-foreground hover:text-primary">
                           <Link to="/emissions">
                             <Video className="w-3 h-3 me-1"/> {lang === 'ar' ? "برامج المرحلة" : "Les Émissions du Moment"}
                           </Link>
                        </Button>
                        <Button variant="link" size="sm" asChild className="text-xs text-muted-foreground hover:text-primary">
                           <a href="/partis">
                             <Building className="w-3 h-3 me-1"/> {lang === 'ar' ? "الأحزاب السياسية" : "Partis politiques"}
                           </a>
                        </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
};

export default ElectoralMap;
